import * as React from 'react';
import './LandingPage.css';
import WaitlistForm from '../components/WaitlistForm';
import PricingCard from '../components/PricingCard';
import { apiRequest } from '../api/client';
import TypewriterText from '../components/TypedText';
import InfoCard from '../components/InfoCard';
// Lucide icons
import { Timer, DollarSign, Brain, Zap, Search, FlaskConical, RefreshCcw, ShieldCheck } from 'lucide-react';
import AnimatedWorkflow  from '../components/AnimatedWorkflow';
import AnimatedPipelineWorkflow from '../components/AnimatedPipelineWorkflow';
interface Plan {
  title: string;
  price: string;
  features: string[];
  highlight?: boolean;
}

const TIME_STATS = [
  { label: "Dev hours lost per month on pipelines", value: "8–20+", highlight: true, desc: "YAML, debugging, docs, reviews. It adds up fast." },
  { label: "Avg test authoring/maintenance (per dev)", value: "~30 hr/mo", highlight: false, desc: "Industry average for writing and maintaining tests." },
  { label: "Team test labor cost", value: "$11,000/mo", highlight: false, desc: "For a 5-person team, just on test authoring & maintenance." },
];

const VALUE_PROP_CARDS = [
  {
    icon: <Timer size={80} strokeWidth={2.2} color="white" />,
    title: "30+ hours/month saved per dev",
    desc: "Pipelyze automates config and test writing, so you can focus on building features.",
  },
  {
    icon: <DollarSign size={80} strokeWidth={2.2} color="white" />,
    title: "$11,000+/mo labor cost avoided",
    desc: "For a team of 5, that’s real money back in your engineering budget.",
  },
  {
    icon: <Brain size={80} strokeWidth={2.2} color="white" />,
    title: "AI-generated tests, always up-to-date",
    desc: "Pipelyze keeps your codebase covered—even as your stack evolves.",
  },
];

const FEATURES = [
  {
    icon: <Zap size={80} strokeWidth={2.2} color="white" />,
    title: "One-click CI/CD",
    desc: "Instant GitHub Actions setup. No YAML. No docs. No guesswork.",
  },
  {
    icon: <Search size={80} strokeWidth={2.2} color="white" />,
    title: "Framework Detection",
    desc: "Detects languages, frameworks, and test tools automatically.",
  },
  {
    icon: <FlaskConical size={80} strokeWidth={2.2} color="white" />,
    title: "Test Generation",
    desc: "AI-generated tests for your code, ready to PR in seconds.",
  },
  {
    icon: <RefreshCcw size={80} strokeWidth={2.2} color="white" />,
    title: "Auto Maintenance",
    desc: "Auto-updates CI for new dependencies, tools, or stack changes.",
  },
  {
    icon: <ShieldCheck size={80} strokeWidth={2.2} color="white" />,
    title: "Secure by Default",
    desc: "Follows security best-practices in all generated pipelines.",
  },
];

const typewriterPhrases = [
  "CI/CD Pipelines",
  "Test Generation",
  "DevOps"
];

const CARD_PURPLE = "#7c3aed7e";
const CARD_PURPLE2 = "#ac47e7a8"; // for features, a lighter purple

const LandingPage: React.FC = () => {
  const [plans, setPlans] = React.useState<Plan[]>([]);
  React.useEffect(() => {
    apiRequest<Plan[]>('/plans')
      .then(setPlans)
      .catch(console.error);
  }, []);
  return (
    <div className="landing-page">
      <div className="gradient-bg"></div>
      {/* HERO */}
      <section className="hero-section fade-in">
        <h1 className="hero-animated-text">
          <span>Focus more on your features</span><br />
          <span>Less on </span><TypewriterText strings={typewriterPhrases} />
        </h1>
        <p className="subtitle">
          <span>Pipelyze builds, tests, and deploys your code without the manual DevOps grind.</span>
          <br />
          <span className="subtitle-highlight"><b>Stop writing pipeline configs—start shipping features.</b></span>
        </p>
        <div className="stats-row">
          {TIME_STATS.map((stat, i) => (
            <div className={`stat-block${stat.highlight ? ' stat-highlight' : ''}`} key={i}>
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
              <div className="stat-desc">{stat.desc}</div>
            </div>
          ))}
        </div>
        <WaitlistForm />
      </section>
      {/* VALUE PROPS */}
      <section className="value-prop-section fade-in">
        <h2>
          <span className="highlighted">Less time on DevOps,</span> more time shipping code
        </h2>
        <div className="value-prop-row">
          {VALUE_PROP_CARDS.map((card, i) => (
            <InfoCard
              key={i}
              icon={card.icon}
              title={card.title}
              desc={card.desc}
              bgColor={CARD_PURPLE}
            />
          ))}
        </div>
      </section>
      <section className="diagram-section fade-in">
        <AnimatedPipelineWorkflow  />
      </section>
      {/* FEATURES */}
      <section className="features-section fade-in">
        <h2>Features</h2>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <InfoCard
              key={i}
              icon={f.icon}
              title={f.title}
              desc={f.desc}
              bgColor={CARD_PURPLE2}
            />
          ))}
        </div>
      </section>
      {/* PRICING */}
      <section className="pricing-section fade-in">
        <h2>Pricing</h2>
        <div className="pricing-grid">
          {plans.map((plan, i) => (
            <div key={plan.title} className="fade-in" style={{ animationDelay: `${0.2 + i * 0.2}s` }}>
              <PricingCard
                title={plan.title}
                price={plan.price}
                features={plan.features}
                highlight={plan.highlight}
                hideButton={true}
                disabled
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
